// Carga dos destinos de análise (GA4, Microsoft Clarity e PostHog) condicionada
// ao consentimento de lib/consent.ts. Antes ficavam em <script> no index.html e
// carregavam para todo mundo, antes de qualquer escolha.
//
// Antes do aceite (ou com recusa):
//   • GA4: só o stub do gtag com Consent Mode v2 tudo "denied". O gtag.js não é
//     baixado, e track()/trackPageView() não empilham nada (lib/analytics.ts).
//   • Clarity: a tag não é baixada. Se já estava carregada (revogação na mesma
//     aba), recebe clarity("consent", false), que encerra a gravação.
//   • PostHog: a lib não é inicializada; se já estava, entra em opt-out.
// Depois do aceite: Consent Mode passa analytics_storage a "granted" (anúncios
// ficam negados: o Disk não faz publicidade), e as três tags carregam.
//
// Máscara: o Clarity grava a tela, e a tela do Disk tem dado de lead de
// terceiro (nome, telefone, e-mail, transcrição). O atributo data-clarity-mask
// no <html> mascara o texto e os inputs de TODO o documento, inclusive dos
// portais (dialogs, sheets, toasts), que são renderizados no <body>, fora do
// #app. O index.html já traz o atributo; aqui ele é reposto antes de carregar a
// tag, para nenhum código que mexa no <html> desfazer a máscara.
import { GA_MEASUREMENT_ID } from "./analytics.js";
import { hasAnalyticsConsent, subscribeConsent, type ConsentChoice } from "./consent.js";
import { initPostHog, phOptOut } from "./posthog.js";

export const CLARITY_PROJECT_ID = (import.meta.env.VITE_CLARITY_PROJECT_ID ?? "").trim();

export interface AnalyticsIds {
  gaId: string;
  clarityId: string;
}

const DEFAULT_IDS: AnalyticsIds = { gaId: GA_MEASUREMENT_ID.trim(), clarityId: CLARITY_PROJECT_ID };

type Queued = ((...args: unknown[]) => void) & { q?: unknown[] };

let ids: AnalyticsIds = DEFAULT_IDS;
let gaLoaded = false;
let clarityLoaded = false;
let unsubscribe: (() => void) | null = null;

const DENIED = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
} as const;

export function applyClarityMask(): void {
  document.documentElement.setAttribute("data-clarity-mask", "true");
}

function injectScript(src: string): void {
  const script = document.createElement("script");
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

function ensureGtagStub(): void {
  const w = window as Window & { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer ?? [];
  w.gtag =
    w.gtag ??
    function gtag() {
      // O gtag.js exige o objeto `arguments`, não um array.
      // oxlint-disable-next-line prefer-rest-params
      (w.dataLayer as unknown[]).push(arguments);
    };
}

function loadGa(gaId: string): void {
  if (gaLoaded) return;
  gaLoaded = true;
  injectScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`);
  window.gtag?.("js", new Date());
  // send_page_view:false — SPA. O page_view sai por rota de
  // lib/analytics-listeners.tsx, inclusive o da carga inicial.
  window.gtag?.("config", gaId, { send_page_view: false });
}

function loadClarity(clarityId: string): void {
  applyClarityMask();
  if (clarityLoaded) return;
  clarityLoaded = true;
  const stub: Queued = function clarity(...args: unknown[]) {
    (stub.q = stub.q ?? []).push(args);
  };
  window.clarity = window.clarity ?? stub;
  injectScript(`https://www.clarity.ms/tag/${encodeURIComponent(clarityId)}`);
}

// Revogação: apaga os cookies primários que as tags já tinham gravado. Tenta o
// host e cada domínio pai, porque o GA grava no domínio registrável.
const ANALYTICS_COOKIE = /^(_ga|_gid|_gat|_clck|_clsk|ph_)/;

function clearAnalyticsStorage(): void {
  const hostParts = window.location.hostname.split(".");
  const domains = hostParts.map((_, i) => hostParts.slice(i).join("."));
  for (const pair of document.cookie ? document.cookie.split(";") : []) {
    const name = pair.split("=")[0]?.trim();
    if (!name || !ANALYTICS_COOKIE.test(name)) continue;
    const expire = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    document.cookie = expire;
    for (const domain of domains) document.cookie = `${expire}; domain=${domain}`;
  }
  try {
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const key = localStorage.key(i);
      if (key && (key.startsWith("ph_") || key.startsWith("_cl"))) localStorage.removeItem(key);
    }
  } catch {
    // storage bloqueado: nada a limpar
  }
}

export function applyAnalyticsConsent(choice: ConsentChoice | null): void {
  if (choice === "granted") {
    if (ids.gaId) {
      window.gtag?.("consent", "update", { analytics_storage: "granted" });
      loadGa(ids.gaId);
    }
    if (ids.clarityId) {
      loadClarity(ids.clarityId);
      window.clarity?.("consent", true);
    }
    initPostHog();
    return;
  }

  // Recusado ou sem escolha. Só há o que desligar se algo já tinha carregado.
  window.gtag?.("consent", "update", DENIED);
  if (clarityLoaded) window.clarity?.("consent", false);
  phOptOut();
  if (choice === "denied") clearAnalyticsStorage();
}

// Chamado uma vez no boot (src/main.tsx), antes do render.
export function initAnalytics(overrides: Partial<AnalyticsIds> = {}): void {
  ids = { ...DEFAULT_IDS, ...overrides };
  applyClarityMask();
  if (ids.gaId) {
    ensureGtagStub();
    // Consent Mode v2: o padrão é negar tudo, antes de qualquer outro comando.
    window.gtag?.("consent", "default", DENIED);
  }
  // Sem aceite guardado não há nada a desligar: o padrão acima já nega tudo.
  if (hasAnalyticsConsent()) applyAnalyticsConsent("granted");

  unsubscribe?.();
  let last = hasAnalyticsConsent();
  unsubscribe = subscribeConsent(() => {
    const now = hasAnalyticsConsent();
    if (now === last) return;
    last = now;
    applyAnalyticsConsent(now ? "granted" : "denied");
  });
}

// Só para testes.
export function resetAnalyticsLoaderForTests(): void {
  unsubscribe?.();
  unsubscribe = null;
  gaLoaded = false;
  clarityLoaded = false;
  ids = DEFAULT_IDS;
}
